import { View } from 'react-native';
import TextLato from '../../../text/text-lato/TextLato';
import { useStore } from '../../../../contexts/StoreContext';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { useEffect } from 'react';
import { neutrals } from 'src/theme/colors';
import { DYNAMIC_MODAL_ACTIONS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { style } from './ModalQualityAlert.style';
import { ModalQualityAlertSuggestions } from './modal-quality-alert-suggestions/ModalQualityAlertSuggestions';
import { Icon } from '../../../icon/Icon';
import { ICON_NAMES } from '../../../icon/icon.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { CtasType, KEEP_ANYWAY } from './ModalQualityAlert.constants';
import BaseModal from '../../base-modal/BaseModal';
import { DynamicModalProps } from '../DynamicModal.types';
import { useRoute } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { Illustration } from '@photosi/rn-components/illustration/Illustration';
import { ILLUSTRATION_NAMES } from '@photosi/rn-components/illustration/illustration.helper';

export const ModalQualityAlert = ({
  type,
  dynamicModalVisible,
  dynamicModalLayer: {
    modalQualityCtas,
    modalQualitySuggestions,
    onPressContinueToCart,
    modalQualityOpenPoint,
    actionCallback,
    qualityAlertUserImage,
    id,
  },
}: DynamicModalProps) => {
  const {
    i18n,
    project: { activeProduct, activeProject },
    dynamicModal: { closeModal },
  } = useStore();
  const route = useRoute();
  const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);
  const productType = activeProduct?.maxUserImages! > 1 ? 'multi_product' : 'single_product';
  const modalQualityOptions = modalQualitySuggestions?.options?.filter((item: string) => item !== KEEP_ANYWAY) ?? [];
  const modalQualityOptionsCount = modalQualityOptions.length;
  const showQualityAlertSuggestions = modalQualityOptionsCount > 0 && (newRoute ? qualityAlertUserImage : true);
  const isSingleCta = modalQualityCtas === CtasType.IGNORE || modalQualityCtas === CtasType.CLOSE;

  const onCloseModal = () => {
    amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
      mode: productType,
      source: modalQualityOpenPoint,
      option: 'close',
    });
    closeModal();
  };

  const handleClose = async () => {
    qualityAlertUserImage && activeProject?.deactivateQualityAlert([qualityAlertUserImage?.id]);
    // when the user clicks on the close button
    onCloseModal();
    actionCallback(DYNAMIC_MODAL_ACTIONS.CLOSE);
  };

  const handleIgnore = async () => {
    // when the user clicks on the ignore button
    onCloseModal();
    onPressContinueToCart();
    actionCallback(DYNAMIC_MODAL_ACTIONS.IGNORE);
  };

  const handleExit = async () => {
    // when the user click on X icon
    onCloseModal();
    actionCallback(DYNAMIC_MODAL_ACTIONS.EXIT);
  };

  const handleHideModal = () => {
    // when the user click outside of the modal
    onCloseModal();
    actionCallback(DYNAMIC_MODAL_ACTIONS.HIDE_MODAL);
  };

  const onPressAbort = async () => {
    amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
      mode: productType,
      source: modalQualityOpenPoint,
      option: 'cancel',
    });
    closeModal();
    actionCallback(DYNAMIC_MODAL_ACTIONS.CANCEL);
  };

  const onPressContinue = async () => {
    amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
      mode: productType,
      source: modalQualityOpenPoint,
      option: 'continue',
    });
    onPressContinueToCart();
    actionCallback(DYNAMIC_MODAL_ACTIONS.CONTINUE);
  };

  useEffect(() => {
    amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertView, {
      mode: productType,
      source: modalQualityOpenPoint,
    });
  }, []);

  return (
    <BaseModal
      testID={TestIDs.QUALITY_ALERT}
      modalWidth={420}
      modalIcon={
        showQualityAlertSuggestions ? (
          <View style={style.qualityIconMain}>
            <View style={style.icon}>
              <Icon name={ICON_NAMES.Alert} color={neutrals.white} />
            </View>
          </View>
        ) : (
          <View style={style.qualityIcon}>
            <Illustration name={ILLUSTRATION_NAMES.LowQualitySpecialImage} />
          </View>
        )
      }
      title={i18n.t('modal-quality-alert:suboptimal-quality')}
      body={
        showQualityAlertSuggestions ? (
          <>
            <TextLato style={style.description}>{i18n.t('modal-quality-alert:suboptimal-solution')}</TextLato>
            <ModalQualityAlertSuggestions
              modalQualitySuggestions={modalQualitySuggestions}
              qualityAlertUserImage={qualityAlertUserImage}
              modalQualityOpenPoint={modalQualityOpenPoint}
              actionCallback={actionCallback}
            />
          </>
        ) : (
          <View style={style.descriptionContainer}>
            <TextLato style={style.description}>
              {i18n.t('modal-quality-alert:sub-quality-multiple-description')}
            </TextLato>
            <TextLato style={style.subDescription}>
              {i18n.t('modal-quality-alert:sub-quality-multiple-subdescription')}
            </TextLato>
          </View>
        )
      }
      discardText={isSingleCta ? undefined : i18n.t('modal-quality-alert:fix')}
      handleDiscard={isSingleCta ? undefined : onPressAbort}
      confirmText={
        isSingleCta
          ? i18n.t(modalQualityCtas === CtasType.CLOSE ? 'modal-quality-alert:close' : 'modal-quality-alert:ignore')
          : i18n.t('modal-quality-alert:ignore')
      }
      handleConfirm={
        isSingleCta ? (modalQualityCtas === CtasType.IGNORE ? handleIgnore : handleClose) : onPressContinue
      }
      onClose={handleExit}
      onCloseOutside={handleHideModal}
      isVisible={dynamicModalVisible}
      type={type}
      id={id}
    />
  );
};
